import { supabase } from '@/lib/supabase';
import type { DataTableCellTone } from '@shared/types/views';

/**
 * F5b: working together on an app's objects — labels, folders across apps,
 * comments with mentions, notifications. Writes go through RPCs; reads
 * follow the app's access.
 */
export interface AppLabel {
  id: string;
  name: string;
  tone: DataTableCellTone;
}

export interface AppComment {
  id: string;
  authorUserId: string;
  body: string;
  createdAt: string;
  mentions: Array<{ kind: 'user' | 'team'; id: string }>;
}

export interface AppFolder {
  id: string;
  name: string;
  description: string | null;
  visibility: 'private' | 'team' | 'org';
  teamId: string | null;
  ownerUserId: string;
  itemCount: number;
}

export interface AppNotification {
  id: string;
  kind: 'mentioned' | 'assigned' | 'commented';
  appKey: string;
  appTitle: string;
  recordId: string | null;
  actorUserId: string | null;
  excerpt: string | null;
  readAt: string | null;
  createdAt: string;
}

const ERRORS: Record<string, string> = {
  app_label_forbidden: 'Dans cette application, seuls le créateur et les managers créent les étiquettes.',
  app_label_exists: 'Cette étiquette existe déjà.',
  app_comment_mention_forbidden: 'Vous ne pouvez mentionner que des personnes ou équipes qui ont accès à l’application.',
  app_comment_invalid: 'Le commentaire est vide ou trop long.',
  app_folder_forbidden: 'Vous ne pouvez pas modifier ce dossier.',
  app_folder_team_invalid: 'Choisissez une équipe pour un dossier d’équipe.',
  app_record_forbidden: 'Vous n’avez pas accès à cette application.',
};

const check = (error: { message: string } | null, fallback: string) => {
  if (error) throw new Error(ERRORS[error.message] ?? fallback);
};

// ---------- Labels ----------
export const loadAppLabels = async (appId: string): Promise<{ labels: AppLabel[]; byRecord: Record<string, string[]>; policy: 'managers' | 'members' }> => {
  const [labels, links, collection] = await Promise.all([
    supabase.from('app_labels').select('id, name, tone').eq('tool_app_id', appId).order('name'),
    supabase.from('app_record_labels').select('record_id, label_id').eq('tool_app_id', appId),
    supabase.from('app_collections').select('label_policy').eq('tool_app_id', appId).eq('key', 'main').maybeSingle(),
  ]);
  check(labels.error ?? links.error ?? collection.error, 'Impossible de charger les étiquettes.');
  const byRecord: Record<string, string[]> = {};
  for (const link of links.data ?? []) (byRecord[link.record_id as string] ??= []).push(link.label_id as string);
  return { labels: (labels.data ?? []) as AppLabel[], byRecord, policy: (collection.data?.label_policy as 'managers' | 'members' | undefined) ?? 'managers' };
};

export const createAppLabel = async (appId: string, name: string, tone: DataTableCellTone) => {
  const { error } = await supabase.rpc('create_app_label_f5b', { p_tool_app_id: appId, p_name: name, p_tone: tone });
  check(error, 'L’étiquette n’a pas pu être créée.');
};

export const setAppRecordLabel = async (recordId: string, labelId: string, on: boolean) => {
  const { error } = await supabase.rpc('set_app_record_label_f5b', { p_record_id: recordId, p_label_id: labelId, p_on: on });
  check(error, 'L’étiquette n’a pas pu être modifiée.');
};

// ---------- Comments ----------
export const loadRecordComments = async (appId: string, recordId: string): Promise<AppComment[]> => {
  const { data: thread } = await supabase.from('tool_threads').select('id')
    .eq('tool_app_id', appId).eq('resource_kind', 'record').eq('resource_key', recordId).maybeSingle();
  if (!thread) return [];
  const { data, error } = await supabase.from('tool_messages')
    .select('id, author_user_id, body, created_at, tool_mentions(principal_kind, principal_id)')
    .eq('thread_id', thread.id).order('created_at');
  check(error, 'Impossible de charger les commentaires.');
  return (data ?? []).map((row) => ({
    id: row.id as string, authorUserId: row.author_user_id as string, body: row.body as string, createdAt: row.created_at as string,
    mentions: ((row.tool_mentions ?? []) as Array<{ principal_kind: 'user' | 'team'; principal_id: string }>)
      .map((mention) => ({ kind: mention.principal_kind, id: mention.principal_id })),
  }));
};

export const postRecordComment = async (recordId: string, body: string, mentions: Array<{ kind: 'user' | 'team'; id: string }>) => {
  const { error } = await supabase.rpc('post_app_record_comment_f5b', {
    p_record_id: recordId, p_body: body,
    p_mentions: mentions.map((mention) => ({ principalKind: mention.kind, principalId: mention.id })),
  });
  check(error, 'Le commentaire n’a pas pu être publié.');
};

// ---------- Folders ----------
export const loadAppFolders = async (): Promise<AppFolder[]> => {
  const { data, error } = await supabase.from('app_folders')
    .select('id, name, description, visibility, team_id, owner_user_id, app_folder_items(count)').order('name');
  check(error, 'Impossible de charger les dossiers.');
  return (data ?? []).map((row) => ({
    id: row.id as string, name: row.name as string, description: row.description as string | null,
    visibility: row.visibility as AppFolder['visibility'], teamId: row.team_id as string | null, ownerUserId: row.owner_user_id as string,
    itemCount: ((row.app_folder_items as Array<{ count: number }> | null)?.[0]?.count) ?? 0,
  }));
};

export const saveAppFolder = async (folder: { id?: string; name: string; description?: string; visibility: AppFolder['visibility']; teamId?: string | null }): Promise<string> => {
  const { data, error } = await supabase.rpc('save_app_folder_f5b', {
    p_folder_id: folder.id ?? null, p_name: folder.name, p_description: folder.description ?? null,
    p_visibility: folder.visibility, p_team_id: folder.teamId ?? null,
  });
  check(error, 'Le dossier n’a pas pu être enregistré.');
  return (data as { id: string }).id;
};

export const deleteAppFolder = async (folderId: string) => {
  const { error } = await supabase.rpc('delete_app_folder_f5b', { p_folder_id: folderId });
  check(error, 'Le dossier n’a pas pu être supprimé.');
};

export const placeInAppFolder = async (folderId: string, recordId: string, options: { position?: number; remove?: boolean } = {}) => {
  const { error } = await supabase.rpc('place_in_app_folder_f5b', {
    p_folder_id: folderId, p_record_id: recordId, p_position: options.position ?? null, p_remove: options.remove ?? false,
  });
  check(error, 'Le dossier n’a pas pu être modifié.');
};

// ---------- Notifications ----------
export const loadAppNotifications = async (): Promise<AppNotification[]> => {
  const { data, error } = await supabase.from('app_notifications')
    .select('id, kind, record_id, actor_user_id, excerpt, read_at, created_at, tool_apps(key, title)')
    .order('created_at', { ascending: false }).limit(30);
  check(error, 'Impossible de charger vos notifications.');
  return (data ?? []).map((row) => {
    const app = row.tool_apps as unknown as { key: string; title: string } | null;
    return {
      id: row.id as string, kind: row.kind as AppNotification['kind'], appKey: app?.key ?? '', appTitle: app?.title ?? '',
      recordId: row.record_id as string | null, actorUserId: row.actor_user_id as string | null,
      excerpt: row.excerpt as string | null, readAt: row.read_at as string | null, createdAt: row.created_at as string,
    };
  });
};

export const markAppNotificationsRead = async (ids: string[] | null) => {
  const { error } = await supabase.rpc('read_app_notifications_f5b', { p_ids: ids });
  check(error, 'Impossible de marquer comme lu.');
};
